import React, { useEffect } from 'react';
import { useBraze } from '@/providers/BrazeProvider';
import { theme } from '@/styles';
import _ from 'lodash';
import styled from 'styled-components';
import useShopDetail from './hooks/useShopDetail';
import AgencyContent from './ui/AgencyContent';
import FloatingInfo from './ui/FloatingInfo';
import ParkContent from './ui/ParkContent';
import ShopDetailCustomerReview from '../ShopDetailCustomerReview/ShopDetailCustomerReview';
import ShopMap from './ui/ShopMap';
import { Text } from '@design-system/pc';

export interface ShopDetailProps {
  shopId: number;
  dispTemplateNo?: string;
}

const ShopDetail = ({ shopId, dispTemplateNo }: ShopDetailProps) => {
  const {
    shopDetail,
    level,
    handleLevel,
    constructList,
    handleScrap,
    isLoading,
    isScrap,
    tabState,
    setTabState,
    handleLinkOpen,
    handleConsult,
    handleConstructionPopup,
    isOpenConsultPopup,
    handleOpenConsultPopup,
    handleCloseConsultPopup,
  } = useShopDetail({
    shopId,
  });
  const { logCustomEvent } = useBraze();

  useEffect(() => {
    if (shopDetail !== undefined) {
      logCustomEvent({
        eventName: 'bz_view_store',
        eventProperties: {
          bz_store_type: _.get(shopDetail, 'bottomBar.type.name', ''),
          bz_store_area: _.get(shopDetail, 'header.address.basic', ''),
          bz_store_num: _.toString(_.get(shopDetail, 'shopId', 0)),
          bz_store_name: _.get(shopDetail, 'header.name', ''),
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shopDetail]);

  return (
    <>
      {shopDetail && (
        <Container>
          <ShopDetailContainer
            borderState={
              shopDetail?.viewType === 1 || shopDetail?.viewType === 2
            }
          >
            <ShopContentsContainer>
              {shopDetail?.viewType === 1 || shopDetail?.viewType === 2 ? (
                <ParkContent
                  data={shopDetail}
                  isLoading={isLoading}
                  constructData={constructList}
                  tabState={tabState}
                  handleConstructionPopup={handleConstructionPopup}
                  handleLinkOpen={handleLinkOpen}
                  setTabState={setTabState}
                />
              ) : (
                <AgencyContent
                  data={shopDetail}
                  isLoading={isLoading}
                  dispTemplateNo={dispTemplateNo}
                  handleConstructionPopup={handleConstructionPopup}
                  constructData={constructList}
                />
              )}
            </ShopContentsContainer>
            <FloatingInfo
              data={shopDetail}
              handleScrap={handleScrap}
              handleConsult={handleConsult}
              isScrap={isScrap}
              isOpenConsultPopup={isOpenConsultPopup}
              handleOpenConsultPopup={handleOpenConsultPopup}
              handleCloseConsultPopup={handleCloseConsultPopup}
            />
          </ShopDetailContainer>

          {shopDetail?.sldToCd && (
            <ShopDetailCustomerReview sldToCd={shopDetail.sldToCd} />
          )}

          <ContentContainer>
            <BottomText variant="subTitle4" color="color_chip.primary">
              매장위치
            </BottomText>
            <ShopMap
              level={level}
              handleLevel={handleLevel}
              center={shopDetail?.header.address?.coordinates}
              name={shopDetail?.header?.name}
              shopType={shopDetail?.header?.type?.groupCode}
            />
          </ContentContainer>
          {shopDetail?.header?.vrView.yn === 'Y' && (
            <ContentContainer>
              <BottomText variant="subTitle4" color="color_chip.primary">
                매장 720VR
              </BottomText>
              <BottomImg>
                <iframe
                  src={shopDetail?.header?.vrView.url}
                  width={'1170px'}
                  height={'500px'}
                ></iframe>
              </BottomImg>
            </ContentContainer>
          )}
        </Container>
      )}
    </>
  );
};

export default ShopDetail;

const Container = styled.div`
  width: 100%;
  padding-bottom: 100px;
`;

const ShopDetailContainer = styled.div<{ borderState: boolean }>`
  display: flex;
  position: relative;
  margin-bottom: 80px;
  gap: 44px;
  border-bottom: ${({ borderState }) =>
    borderState
      ? `1px solid ${theme.colors.neutral_colors.gray[200]}`
      : 'none'};
  padding-bottom: ${({ borderState }) => (borderState ? `80px` : '0px')};
`;

const ShopContentsContainer = styled.div`
  width: 774px;
`;

const BottomImg = styled.div`
  width: 100%;
  height: 500px;
`;

const ContentContainer = styled.div`
  margin-bottom: 80px;
`;

const BottomText = styled(Text)`
  display: block;
  margin-bottom: 24px;
`;
